import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';

// Edits booking capacity & turnaround timing only. Opening hours are managed
// on the dedicated Opening Hours admin page (OpeningHours entity).
export default function VenueSettingsEditor() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['venue-settings'],
    queryFn: () => base44.entities.VenueSettings.list(),
  });
  const existing = settings[0];

  useEffect(() => {
    if (form !== null) return;
    if (settings.length === 0) {
      setForm({
        concurrent_capacity: 4,
        slot_duration_minutes: 60,
        buffer_minutes: 0,
        notes: '',
      });
      return;
    }
    const s = settings[0];
    setForm({
      concurrent_capacity: s.concurrent_capacity ?? 4,
      slot_duration_minutes: s.slot_duration_minutes ?? 60,
      buffer_minutes: s.buffer_minutes ?? 0,
      notes: s.notes || '',
    });
  }, [settings, form]);

  if (isLoading || !form) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (existing) {
        await base44.entities.VenueSettings.update(existing.id, form);
      } else {
        await base44.entities.VenueSettings.create(form);
      }
      queryClient.invalidateQueries({ queryKey: ['venue-settings'] });
      toast({ title: 'Capacity settings saved' });
    } catch (e) {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
    setSaving(false);
  };

  const field = "bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60";

  return (
    <div className="bg-[#111115] border border-white/5 p-6 space-y-6 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Concurrent Capacity (per slot)</Label>
          <Input type="number" min={1} value={form.concurrent_capacity} onChange={(e) => set('concurrent_capacity', Number(e.target.value))} className={field} />
          <p className="text-white/30 text-[10px]">Max bookings per time slot (rigs / staffed stations).</p>
        </div>
        <div className="space-y-1.5">
          <Label>Slot Duration (mins)</Label>
          <Input type="number" min={15} step={15} value={form.slot_duration_minutes} onChange={(e) => set('slot_duration_minutes', Number(e.target.value))} className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Turnaround Buffer (mins)</Label>
          <Input type="number" min={0} step={5} value={form.buffer_minutes} onChange={(e) => set('buffer_minutes', Number(e.target.value))} className={field} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className={field} />
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-5 py-3 transition-all disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE SETTINGS
      </button>
    </div>
  );
}